import { useCallback, useContext, useEffect, useRef, useState } from "react"

//Kiosk components
import KioskAdminFormWizard from "../FormWizard/KioskAdminFormWizard"
import {
  kioskApiGetFormProfile,
  kioskApiGetForms,
  kioskApiPutUpdateForm,
} from "../../kioskApi"

//PrimeReact
import { Button } from "primereact/button"
import { Dropdown } from "primereact/dropdown"
import { InputText } from "primereact/inputtext"
import { FilterMatchMode } from "primereact/api"
import { InputSwitch } from "primereact/inputswitch"

//SOAR Components
import { UserContext } from "../../../../context/userContext"
import { LogError } from "../../../../helpers/logger"

//Styles
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"
import myStyles from "../../../../styles/kiosk/KioskAdminForm.module.css"
import DeleteKioskFormDialog from "./DeleteKioskFormDialog"
import { Tooltip } from "primereact/tooltip"
import { LoaderMedium } from "../../../../components/Loaders"
import InactiveKioskLabel from "../InactiveKioskLabel"
import CustomTable from "../CustomComponents/CustomTable"

export default function KioskAdminFormList({
  kiosk_organization_id,
  organization_id,
  isLoadingTemplates,
  templates,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(true)
  const [refreshFormList, setRefreshFormList] = useState(false)
  const [forms, setForms] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editFormId, setEditFormId] = useState(undefined)
  const [deleteFormId, setDeleteFormId] = useState(undefined)

  const [listFilters, setListFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    formName: { value: null, matchMode: FilterMatchMode.EQUALS },
    activeForms: true,
    sortField: "name",
    sortOrder: 1,
  })

  const formsRef = useRef([])

  useEffect(() => {
    formsRef.current = forms
  }, [forms])

  const loadForms = useCallback(async () => {
    try {
      if (kiosk_organization_id) {
        setIsLoading(true)
        const kioskForms = await kioskApiGetForms(
          userCtx.tokenKiosk,
          kiosk_organization_id
        )

        setForms(kioskForms?.forms || [])

        setIsLoading(false)
      } else {
        setForms([])
        setIsLoading(false)
      }
    } catch (error) {
      LogError("Unable to load Kiosk forms", error)
      userCtx.setNotificationError(
        "Unable to load Kiosk forms for this organization"
      )
      setForms([])
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosk_organization_id, refreshFormList])

  useEffect(() => {
    loadForms()
  }, [loadForms])

  const toggleFormActiveStatus = async (profile) => {
    try {
      let index = forms.indexOf(profile)

      if (index >= 0) {
        let newForms = [...formsRef.current]
        newForms[index].active = !profile.active
        newForms[index].updating = true
        setForms(newForms)

        const formProfile = await kioskApiGetFormProfile(
          userCtx.tokenKiosk,
          profile.id
        )
        formProfile.active = newForms[index].active
        formProfile.userId = userCtx.session.user.staff_id
        await kioskApiPutUpdateForm(userCtx.tokenKiosk, formProfile)

        const form = formsRef.current.find((form) => form.id === profile.id)
        if (form) {
          newForms = [...formsRef.current]
          index = newForms.indexOf(form)
          if (index >= 0) {
            newForms[index].updating = false
            setForms(newForms)
          }
        }
      } else {
        userCtx.setNotificationError(`Option not found: ${profile.name}`)
      }
    } catch (error) {
      LogError("Unable to update FTV profile", error)
      userCtx.setNotificationError(
        `Unable to update option active status: ${profile.name}`
      )
    }
  }

  const StatusBodyTemplate = (rowData) => {
    return (
      <div className={kioskStyles.switchControl}>
        <InputSwitch
          checked={rowData.active}
          disabled={rowData.updating}
          onChange={(e) => toggleFormActiveStatus(rowData)}
        />
      </div>
    )
  }

  const ActionBodyTemplate = (rowData) => {
    return (
      <div className={kioskStyles.actionButtonColumn}>
        <Tooltip
          target={`#form-edit-${rowData.id}`}
          position="bottom"
          className="custom-tooltip"
        >
          <span>Edit</span>
        </Tooltip>
        <Button
          id={`form-edit-${rowData.id}`}
          icon="pi pi-pencil"
          type="button"
          onClick={() => {
            setEditFormId(rowData.id)
            setDeleteFormId(undefined)
            setShowForm(true)
          }}
          className={kioskStyles.roundedButton}
        />
        <Tooltip
          target={`#form-delete-${rowData.id}`}
          position="bottom"
          className="custom-tooltip"
        >
          <span>Delete</span>
        </Tooltip>
        <Button
          id={`form-delete-${rowData.id}`}
          type="button"
          icon="pi pi-trash"
          onClick={() => {
            setEditFormId(undefined)
            setDeleteFormId(rowData.id)
            setShowForm(false)
          }}
          className={kioskStyles.roundedButton}
        />
      </div>
    )
  }

  const TemplateNameBodyTemplate = (rowData) => {
    const templateProfile = templates?.filter(
      (template) => template.template_name === rowData.soarTemplateName
    )

    return (
      <div className={kioskStyles.tableData}>
        {templateProfile?.length > 0
          ? templateProfile[0].template_title
          : rowData.soarTemplateName}
      </div>
    )
  }

  const DescriptionTemplate = (rowData) => {
    return <div className={kioskStyles.tableData}>{rowData.description}</div>
  }

  const NameTemplate = (rowData) => {
    return <div className={kioskStyles.tableData}>{rowData.name}</div>
  }

  const headers = () => {
    return [
      { value: "name", label: "Name", sort: true, dataTemplate: NameTemplate },
      {
        value: "description",
        label: "Description",
        sort: true,
        dataTemplate: DescriptionTemplate,
      },
      {
        value: "soarTemplateName",
        label: "Template Used",
        dataTemplate: TemplateNameBodyTemplate,
        sort: true,
      },
      {
        value: "active",
        label: "Active",
        dataTemplate: StatusBodyTemplate,
        className: myStyles.statusColumn,
        sort: true,
      },
      {
        value: "actions",
        label: undefined,
        dataTemplate: ActionBodyTemplate,
        className: myStyles.actionColumn,
      },
    ]
  }

  return (
    <>
      {deleteFormId ? (
        <DeleteKioskFormDialog
          formId={deleteFormId}
          forms={forms}
          onClose={() => {
            setDeleteFormId(undefined)
          }}
          onSubmit={() => {
            setDeleteFormId(undefined)
            setRefreshFormList(!refreshFormList)
          }}
        />
      ) : null}
      {showForm ? (
        <KioskAdminFormWizard
          templates={templates}
          formId={editFormId}
          kioskOrganizationId={kiosk_organization_id}
          organizationId={organization_id}
          onClose={() => {
            setShowForm(false)
            setEditFormId(undefined)
            setDeleteFormId(undefined)
          }}
          onSubmit={() => {
            //Reset form wizard state
            setShowForm(false)
            setEditFormId(undefined)
            setDeleteFormId(undefined)
            //Toggle the refresh state
            setRefreshFormList(!refreshFormList)
          }}
        />
      ) : null}
      <div className={myStyles.ListLayout}>
        <div className={myStyles.listLayoutAdd}>
          <Button
            label="Add Form"
            disabled={isLoading || isLoadingTemplates || !kiosk_organization_id}
            loading={isLoadingTemplates && kiosk_organization_id}
            type="button"
            className={kioskStyles.button}
            onClick={() => {
              setEditFormId(undefined)
              setDeleteFormId(undefined)
              setShowForm(true)
            }}
          />
        </div>

        {!kiosk_organization_id && organization_id ? (
          <div className={myStyles.listLayoutList}>
            <InactiveKioskLabel />
          </div>
        ) : (
          <>
            <div className={myStyles.listLayoutSearch}>
              <span className={`p-input-icon-right ${myStyles.filterControl}`}>
                <i className="pi pi-search" />
                <InputText
                  value={listFilters.global.value}
                  onChange={(e) => {
                    const newFilters = { ...listFilters }
                    newFilters.global.value = e.target.value

                    setListFilters(newFilters)
                  }}
                  placeholder="Keyword Search"
                  className={myStyles.filterControl}
                />
              </span>
            </div>
            <div className={myStyles.listLayoutSort}>
              <Dropdown
                placeholder="Sort by"
                value={listFilters.sortField}
                options={[
                  { label: "Sort by: Name", value: "name" },
                  { label: "Sort by: Template", value: "soarTemplateName" },
                  { label: "Sort by: Description", value: "description" },
                  { label: "Sort by: Enabled", value: "active" },
                ]}
                onChange={(e) => {
                  setListFilters({
                    ...listFilters,
                    sortField: e.value,
                  })
                }}
                disabled={isLoading}
              />
            </div>

            <div className={myStyles.listLayoutList}>
              {isLoading ? (
                <LoaderMedium />
              ) : (
                <CustomTable
                  headers={headers()}
                  collection={forms || []}
                  sortField={listFilters?.sortField}
                  sortOrder={listFilters?.sortOrder}
                  onSort={(newSort) => {
                    setListFilters({
                      ...listFilters,
                      sortField: newSort.sortField,
                      sortOrder: newSort.sortOrder,
                    })
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}
